import { Injectable } from '@angular/core';
import {
  ComparisonCategoryWithCount,
  ComparisonCharacteristics,
  ComparisonCharacteristicsByProducts,
  ComparisonCharacteristicsNames,
  ComparisonEntry,
  EnrichedComparisonEntry,
} from '@comparison/const';
import { ComparisonActions, ComparisonSelectors, StateWithComparison } from '@comparison/data-access';
import { Store, select } from '@ngrx/store';
import { Category, FeatureValue, Product, ProductScope, ProductService } from '@spartacus/core';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ComparisonService {
  differenceOnly$ = new BehaviorSubject<boolean>(false);
  currentCategorySubject$ = new BehaviorSubject<string>(null);

  productAdded$ = new Subject<string | null>();

  activeCategoryEntries$: Observable<EnrichedComparisonEntry[]> = this.getActiveCategory().pipe(
    switchMap((code) => this.getEntriesByCategory(code)),
  );

  activeCategoryProducts$: Observable<Product[]> = this.activeCategoryEntries$.pipe(
    map((entries) => entries.map((entry) => entry.product)),
  );

  activeCategoryProductsCount$: Observable<number> = this.activeCategoryEntries$.pipe(map((entries) => entries.length));

  allProductsCharacteristics$: Observable<ComparisonCharacteristicsByProducts> = this.activeCategoryEntries$.pipe(
    map((entries: EnrichedComparisonEntry[]) => {
      const characteristics: ComparisonCharacteristicsByProducts = {};

      for (const entry of entries) {
        characteristics[entry.product.code] = {};

        if (entry.rating?.score) {
          characteristics[entry.product.code].rating = {
            name: 'Рейтинг товара',
            value: String(entry.rating.score),
          };
        }

        for (const classification of entry.product.classifications || []) {
          for (const feature of classification.features || []) {
            characteristics[entry.product.code][feature.code] = {
              name: feature.name,
              value: `${
                feature.featureValues
                  .map((item: FeatureValue) => item?.value)
                  .filter((item) => !!item)
                  .join(', ') || ''
              }${feature.featureUnit ? ` ${feature.featureUnit.name}` : ''}`,
            };
          }
        }
      }

      return characteristics;
    }),
  );

  allCharacteristicsNames$ = this.getCharacteristicsNames(this.allProductsCharacteristics$);

  constructor(
    private store: Store<StateWithComparison>,
    private productService: ProductService,
  ) {}

  getCharacteristicsByProducts(products$: Observable<Product[]>): Observable<ComparisonCharacteristicsByProducts> {
    return combineLatest({
      characteristicsByProducts: combineLatest({
        products: products$,
        allProductsCharacteristics: this.allProductsCharacteristics$,
      }).pipe(
        map(({ products, allProductsCharacteristics }) => {
          const characteristicsByProducts: ComparisonCharacteristicsByProducts = {};
          for (const product of products) {
            if (allProductsCharacteristics[product.code]) {
              characteristicsByProducts[product.code] = allProductsCharacteristics[product.code];
            }
          }

          return characteristicsByProducts;
        }),
      ),
      differenceOnly: this.differenceOnly$,
      allNames: this.allCharacteristicsNames$,
    }).pipe(
      map(({ characteristicsByProducts, differenceOnly, allNames }) => {
        if (!differenceOnly) {
          return characteristicsByProducts;
        }

        const result: ComparisonCharacteristicsByProducts = {};

        for (const productCode of Object.keys(characteristicsByProducts)) {
          result[productCode] = {};
        }

        for (const nameCode of Object.keys(allNames)) {
          const values = Object.values(characteristicsByProducts).map(
            (characteristics: ComparisonCharacteristics) => characteristics[nameCode]?.value,
          );

          if (values.length > 0 && !values.every((value: string) => value === values[0])) {
            for (const productCode of Object.keys(characteristicsByProducts)) {
              result[productCode][nameCode] = characteristicsByProducts[productCode][nameCode];
            }
          }
        }

        return result;
      }),
    );
  }

  getCharacteristicsNames(
    characteristicsByProducts$: Observable<ComparisonCharacteristicsByProducts>,
  ): Observable<ComparisonCharacteristicsNames> {
    return characteristicsByProducts$.pipe(
      map((characteristics: ComparisonCharacteristicsByProducts) => {
        const names: ComparisonCharacteristicsNames = {};

        for (const characteristicsByProduct of Object.values(characteristics)) {
          for (const featureCode of Object.keys(characteristicsByProduct)) {
            if (!names[featureCode] && characteristicsByProduct[featureCode]) {
              names[featureCode] = characteristicsByProduct[featureCode].name;
            }
          }
        }

        return names;
      }),
    );
  }

  getEntryLoading(productId: string): Observable<boolean> {
    return this.store.pipe(select(ComparisonSelectors.getComparisonEntryLoading(productId)));
  }

  setCurrentCategory(code: string): void {
    this.currentCategorySubject$.next(code);
  }

  setDifferenceOnly(value: boolean): void {
    this.differenceOnly$.next(value);
  }

  getEntries(): Observable<ComparisonEntry[]> {
    return this.store.pipe(select(ComparisonSelectors.getComparisonEntries));
  }

  getLastAddedCategoryCode(): Observable<string | undefined> {
    return this.store.pipe(select(ComparisonSelectors.getComparisonLastAddedCategoryCode));
  }

  getLastAddedCategoryEntriesCount(): Observable<number> {
    return this.store.pipe(select(ComparisonSelectors.getComparisonLastAddedCategoryEntriesCount));
  }

  getSortedCategories(): Observable<Category[]> {
    return this.store.pipe(select(ComparisonSelectors.getComparisonSortedCategories));
  }

  getSortedCategoriesWithCount(): Observable<ComparisonCategoryWithCount[]> {
    return combineLatest({
      sortedCategories: this.getSortedCategories(),
      entries: this.getEntries(),
    }).pipe(
      map(({ sortedCategories, entries }) =>
        sortedCategories
          .map((category: Category) => ({
            ...category,
            count: this.calcCountOnCategory(entries, category.code),
          }))
          .filter((category) => category.count > 0),
      ),
    );
  }

  getEntriesByCategory(code: string): Observable<EnrichedComparisonEntry[]> {
    return this.getEntries().pipe(
      map((list) => list.filter((item) => item.category.code === code)),
      switchMap((entries) => {
        if (!entries.length) {
          return of([]);
        }

        return combineLatest(
          entries.map((entry) =>
            this.productService.get(entry.productId, ProductScope.LIST).pipe(
              filter<Product>(Boolean),
              map((product) => ({ ...entry, product })),
            ),
          ),
        );
      }),
    );
  }

  hasEntry(productId: string): Observable<boolean> {
    return this.store.pipe(select(ComparisonSelectors.getComparisonHasEntry(productId)));
  }

  addEntry(productId: string): void {
    this.store.dispatch(new ComparisonActions.ComparisonAddEntry({ productId }));
  }

  removeEntry(productId: string): void {
    this.store.dispatch(new ComparisonActions.ComparisonRemoveEntry({ productId }));
  }

  removeCategory(code: string): void {
    this.store.dispatch(new ComparisonActions.ComparisonRemoveCategory({ code }));
  }

  reset(): void {
    this.store.dispatch(new ComparisonActions.ComparisonReset());
  }

  setActiveCategory(categoryCode: string): void {
    this.store.dispatch(new ComparisonActions.ComparisonSetActiveCategory(categoryCode));
  }

  getActiveCategory(): Observable<string> {
    return this.store.pipe(select(ComparisonSelectors.getComparisonActiveCategory));
  }

  setProductAdded(value: string): void {
    this.productAdded$.next(value);
  }

  getProductAdded(): Observable<string | null> {
    return this.productAdded$.asObservable();
  }

  private calcCountOnCategory(entries: ComparisonEntry[], categoryCode: string): number {
    return entries.filter((entry) => entry.category.code === categoryCode).length;
  }
}
